/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { useCallback, useEffect, useRef, useState } from 'react'
import req from '@sportninja/common/api/request'
import colors from '@sportninja/common/constants/appColors'
import { isCanlan } from '@sportninja/common/utils/customer-name'

/**
 * Progress bar component to display loading progress
 * @param {Object} props - Component props
 * @param {number} props.completed - Percentage of completion (0-100)
 * @returns {JSX.Element} Progress bar UI
 */
const ProgressBar = ({ completed }) => {
  return (
    <div
      css={css`
        height: 35px;
        width: '100%';
        background-color: #e0e0de;
        border-radius: 5px;
      `}
    >
      <div
        css={css`
          height: 100%;
          width: ${completed}%;
          background-color: ${colors.DEFAULT_FLAIR};
          border-radius: inherit;
          text-align: right;
          transition: width 1s ease-in-out;
          justify-content: center;
          align-items: center;
          display: flex;
        `}
      >
        <span
          css={css`
            font-family: ${isCanlan
              ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
              : 'BarlowCondensed, Arial, Helvetica, sans-serif;'};
            color: white;
            font-size: 15px;
            font-weight: 'bold';
          `}
        >
          {completed > 0 ? `${completed}%` : ''}
        </span>
      </div>
    </div>
  )
}

let gameIndex = 0
let gamesSuccessfullyProcessed = 0
let lastIndexWithError = -1

/**
 * SignInReportsPrintFirefox component for printing sign-in sheets
 * Implements Firefox-compatible printing strategy
 */
export default function SignInReportsPrintFirefox({ history, location }) {
  const [loading, setLoading] = useState(true)
  const [hasError, setErrorExists] = useState(false)
  const [readyToPrint, setReadyToPrint] = useState(false)
  const [totalGamesProcessed, setTotalGamesProcessed] = useState(-1)
  const [abortControllers, setAbortControllers] = useState<AbortController[]>(
    []
  )
  const [HTMLLength] = useState(
    (location && location.state && location.state.games.length) || 0
  )

  let games = (location && location.state && location.state.games) || []
  // const isSoccer = location.state?.isSoccer || false

  const showRosters = location.state.showRosters
  // check if showRosters is undefined
  const isShowRostersUndefined = typeof showRosters === 'undefined'

  const iframeContainer = useRef<HTMLDivElement>(null)

  const [fontsLoaded, setFontsLoaded] = useState({
    barlowCondensed: false,
    inter: false,
  })

  /**
   * Set up landscape print orientation
   * This ensures sign-in sheets always print correctly
   */
  useEffect(() => {
    const printStyles = document.createElement('style')
    printStyles.textContent = `
      @media print {
        @page {
          size: portrait;
        }
        body {
          margin: 0;
          padding: 0;
        }
      }
    `

    document.head.appendChild(printStyles)

    return () => {
      // Clean up the style element when component unmounts
      if (document.head.contains(printStyles)) {
        document.head.removeChild(printStyles)
      }
    }
  }, [])

  useEffect(() => {
    const isFontLoaded = (fontFamily) => {
      return document.fonts.check(`1em ${fontFamily}`)
    }
    const waitForFonts = async (fontFamilies, retries = 5, delay = 1000) => {
      let fontsLoaded = {}
      for (let i = 0; i < retries; i++) {
        const promises = fontFamilies.map((fontFamily) => {
          return document.fonts.load(`1em ${fontFamily}`).then(() => {
            if (isFontLoaded(fontFamily)) {
              console.log(`${fontFamily} is loaded`)
              fontsLoaded[fontFamily] = true
            } else {
              console.error(`${fontFamily} failed to load`)
              fontsLoaded[fontFamily] = false
            }
          })
        })

        await Promise.all(promises)

        if (Object.values(fontsLoaded).every((loaded) => loaded)) {
          break
        } else {
          await new Promise((resolve) => setTimeout(resolve, delay))
        }
      }

      return fontsLoaded
    }

    const checkFonts = async () => {
      const fonts: any = ['Barlow Condensed', 'Inter']
      const loadedFonts = await waitForFonts(fonts)
      setFontsLoaded({
        barlowCondensed: loadedFonts['Barlow Condensed'],
        // eslint-disable-next-line dot-notation
        inter: loadedFonts['Inter'],
      })
    }

    checkFonts()
  }, [])

  /**
   * Load sign-in sheets for all games and prepare them for printing
   * Uses Firefox-compatible approach with iframe
   */
  const loadGames = useCallback(async () => {
    try {
      const printContents = []

      for (const i in games) {
        if (Number(i) > lastIndexWithError) {
          setTotalGamesProcessed((prev) => prev + 1)
          const abortController = new AbortController()
          const signal = abortController.signal
          setAbortControllers((state) => {
            state.push(abortController)
            return state
          })

          gameIndex = Number(i)

          try {
            const url = isShowRostersUndefined
              ? `/games/${games[Number(i)]}/signin/html`
              : `/games/${games[Number(i)]}/signin/html?show_team_rosters=${
                  showRosters ? '1' : '0'
                }`
            const response = await req(url, {
              parseJSON: false,
              signal,
            })

            const html = await response.text()
            // @ts-ignore
            printContents.push(html)
            gamesSuccessfullyProcessed += 1
          } catch (error) {
            console.error(`Error loading game ${games[Number(i)]}:`, error)
            // Continue with other games
          }
        }
      }

      // Create a single print document with all sign-in sheets
      if (printContents.length > 0) {
        const printFrame = document.createElement('iframe')
        printFrame.style.position = 'absolute'
        printFrame.style.width = '100%'
        printFrame.style.height = '100%'
        printFrame.style.border = 'none'
        printFrame.style.top = '0'
        printFrame.style.left = '0'
        printFrame.id = 'print-frame'

        // Add to container
        iframeContainer.current?.appendChild(printFrame)

        // Create a document with all sign-in sheets, each in its own div with page breaks
        const combinedHTML = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>SignInReportsPrintFirefox</title>
          <style>
            @import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@400;600;700&display=swap');
            @import url('https://fonts.googleapis.com/css2?family=Inter:slnt,wght@-10..0,100..900&display=swap');
            body { margin: 0; padding: 0; background: white; }
            .signin-page {
              page-break-after: always;
              page-break-inside: avoid;
            }
            @page { size: portrait; margin: 0; }
            @media print {
              body { margin: 0; padding: 0; }
            }
          </style>
        </head>
        <body>
          ${printContents
            .map((content) => `<div class="signin-page">${content}</div>`)
            .join('')}
        </body>
        </html>
      `

        // Write to the iframe
        printFrame.onload = () => {
          setTotalGamesProcessed(games.length)
          setReadyToPrint(true)
        }

        const frameDoc = printFrame.contentWindow?.document
        frameDoc?.open()
        frameDoc?.write(combinedHTML)
        frameDoc?.close()
      } else {
        setErrorExists(true)
        setLoading(false)
      }
    } catch (error) {
      if (games.length <= 1) {
        setErrorExists(true)
        setLoading(false)
      } else {
        saveGameWithErrorAndProceedToNextGame()
      }
    }
  }, [isShowRostersUndefined, showRosters])

  const saveGameWithErrorAndProceedToNextGame = useCallback(() => {
    lastIndexWithError = gameIndex

    loadGames()
  }, [loadGames])

  /**
   * Handle printing when content is ready
   */
  useEffect(() => {
    if (readyToPrint && !hasError) {
      setLoading(false)
      setTimeout(() => {
        if (gamesSuccessfullyProcessed > 0) {
          // For Firefox, we need to use the iframe's print function
          const isFirefox = navigator.userAgent.indexOf('Firefox') !== -1

          if (isFirefox) {
            const printFrame = document.getElementById('print-frame')
            // @ts-ignore
            if (printFrame && printFrame.contentWindow) {
              // @ts-ignore
              printFrame.contentWindow.print()
            } else {
              window.print()
            }
          } else {
            window.print()
          }
        }
      }, 500) // Delay to ensure everything is rendered
    }
  }, [readyToPrint, hasError])

  /**
   * Initialize loading of games when fonts are loaded
   */
  useEffect(() => {
    if (fontsLoaded.barlowCondensed && fontsLoaded.inter) {
      loadGames()
    }

    return () => {
      gameIndex = 0
      gamesSuccessfullyProcessed = 0
      lastIndexWithError = -1

      abortControllers.forEach((controller) => {
        controller.abort()
      })
    }
  }, [fontsLoaded, loadGames])

  return (
    <>
      <div
        css={css`
          padding: 50px;
          width: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          overflow: hidden;

          @media only print {
            visibility: hidden;
            display: none; // Hide UI during print
          }
        `}
      >
        {loading ? (
          <>
            <h1
              css={css`
                font-size: 40px;
                font-family: ${isCanlan
                  ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
                  : 'BarlowCondensed, Arial, Helvetica, sans-serif;'};
                margin-bottom: 2em;
                font-weight: bold;
              `}
            >
              Your Sign-In Sheets Are Being Processed. Please Wait.
            </h1>
            <div
              css={css`
                width: 60%;
              `}
            >
              <ProgressBar
                completed={Math.round(
                  (totalGamesProcessed / games.length) * 100
                )}
              />
            </div>
          </>
        ) : (
          <>
            <h1
              css={css`
                font-size: 40px;
                font-family: ${isCanlan
                  ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
                  : 'BarlowCondensed, Arial, Helvetica, sans-serif;'};
                margin-bottom: 2em;
                font-weight: bold;
                text-align: center;
              `}
            >
              {hasError || gamesSuccessfullyProcessed === 0
                ? 'There Is A Problem Processing The Scoresheet, Please Try Again Later.'
                : 'Your Sign-In Sheets Are Ready To Download.'}
            </h1>
            <p
              css={css`
                font-size: 14px;
                font-family: ${isCanlan
                  ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
                  : 'BarlowCondensed, Arial, Helvetica, sans-serif;'};
                margin-bottom: 2.5em;
                font-weight: bold;
              `}
            >
              A Total Of {gamesSuccessfullyProcessed} / {HTMLLength} Sign-In
              Sheets Were Generated.
            </p>
            {gamesSuccessfullyProcessed > 0 && (
              <a
                css={css`
                  font-size: 15px;
                  font-weight: bold;
                  color: ${colors.DEFAULT_FLAIR};
                  margin-left: 8px;
                  margin-bottom: 2.5em;
                  font-family: Arial, Helvetica, sans-serif;
                `}
                onClick={() => {
                  const printFrame = document.getElementById('print-frame')
                  // @ts-ignore
                  if (printFrame && printFrame.contentWindow) {
                    // @ts-ignore
                    printFrame.contentWindow.print()
                  } else {
                    window.print()
                  }
                }}
              >
                {hasError
                  ? `Show The ${gamesSuccessfullyProcessed} Games Processed`
                  : 'Click Here To Open Print Dialog Again.'}
              </a>
            )}
            <button
              type='button'
              onClick={() => {
                games = []
                gamesSuccessfullyProcessed = 0
                gameIndex = 0
                history.goBack()
              }}
              css={css`
                font-family: ${isCanlan
                  ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
                  : 'BarlowCondensed, Arial, Helvetica, sans-serif;'};
                font-size: 24px;
                font-weight: bold;
                color: white;
                margin-left: 8px;
              `}
            >
              {'<'} Back
            </button>
          </>
        )}
      </div>
      <div
        css={css`
          visibility: hidden;
          height: 0;
          overflow: hidden;

          @media print {
            visibility: visible;
            height: auto;
            overflow: visible;
          }
        `}
        ref={iframeContainer}
      />
    </>
  )
}
